// The shared instance: it adds the user's token, which Laila's authorizer
// requires, and refreshes it on a 401.
import { axios, refreshAccessToken } from '@vyce/core/src/api';
import config from '../config';
import { getAccessToken } from '../token-storage';
import { CreateResponseAiRequest } from '../types';
import { LailaChatEvent, parseSse } from './sse';

export type LailaFaq = 'employer' | 'worker';

export interface LailaChatRequest {
  // This turn's question only: the earlier ones live server-side.
  input: string;
  // The `responseId` of the last answer; none starts a conversation.
  previousResponseId?: string;
  instructions?: string;
  model?: string;
  // At most 2 are searched; the server keeps the last ones.
  vectorStoreIds?: string[];
  // Turns on the time tools, which the server runs with the user's token.
  companyId?: string;
  // Voice mode: the server also speaks the answer, in this voice, as `audio` events.
  voice?: string;
  // 'pcm' streams each sentence's voice in pieces as it is made; 'mp3' sends it whole.
  audioFormat?: 'mp3' | 'pcm';
  // The help-centre FAQ Laila answers from; the server reads it into her instructions.
  faq?: LailaFaq;
  // The card kinds this app draws: the server offers only the tools whose cards it can show.
  cards?: string[];
}

// Laila's answer as it is written. axios can't read a response while it
// streams in the browser, so this is fetch, with the same token and refresh.
// Resolves when the stream ends; `onEvent` may throw to abort it.
export const streamLailaChat = async (
  request: LailaChatRequest,
  onEvent: (event: LailaChatEvent) => void,
  signal?: AbortSignal
) => {
  const post = (token: string | null) =>
    fetch(config.aiApi.chat, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(request),
      signal,
    });

  let res = await post(getAccessToken());
  if (res.status === 401) {
    const token = await refreshAccessToken();
    if (token) {
      res = await post(token);
    }
  }
  if (!res.ok || !res.body) {
    throw new Error(`Laila answered ${res.status}`);
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) {
        break;
      }
      const parsed = parseSse(buffer + decoder.decode(value, { stream: true }));
      buffer = parsed.rest;
      parsed.events.forEach(onEvent);
    }
  } finally {
    reader.cancel().catch(() => {});
  }
};

export const createResponseAiRequest = ({
  input,
  model,
  assistantId,
  tools = [],
  signal,
}: CreateResponseAiRequest) => {
  return axios.post(
    config.aiApi.responses,
    {
      input,
      model,
      assistantId,
      tools,
    },
    { signal }
  );
};

export const speechToTextRequest = (audioFile: string) => {
  return axios.post(config.aiApi.speechToText, {
    file: audioFile,
  });
};

// Returns an MP3, base64-encoded, in `body`.
export const textToSpeechRequest = (text: string, voice?: string) => {
  return axios.post(config.aiApi.textToSpeech, {
    text,
    voice,
  });
};

// All of a chat's uploads go into one store: pass the store of its first
// upload, or none to start it. The body says whether the file is searchable yet.
export const createVectorStoreRequest = (file: string, fileName: string, vectorStoreId?: string) => {
  return axios.post(config.aiApi.createVectorStore, {
    file,
    fileName,
    vectorStoreId,
  });
};

export const deleteVectorStoreRequest = (fileId: string, vectorStoreId: string) => {
  return axios.post(config.aiApi.deleteVectorStore, {
    fileId,
    vectorStoreId,
  });
};
